// The device vocabulary the admin surfaces show and filter by — what a project
// was CREATED on, stamped from the creating request's User-Agent
// (backend/recruiter/device.py, migration 0183).
//
// Mirrors the closed sets that module owns. They are closed on BOTH sides, so
// the console lists them from these constants rather than from whatever the
// loaded page happens to contain — options derived from server-filtered rows
// collapse to the values already ticked, leaving no control that could widen
// the selection again.
//
// `unknown` is the filters' extra option and never a stored value: the column
// is NULL for a project with no browser behind it (an import, one accepted from
// a suggestion, an MCP turn) and for every project older than 0183, which today
// is most of the table. An operator asks for that bucket deliberately, so it is
// offered rather than hidden.

export const DEVICE_CLASSES = ["desktop", "mobile", "tablet"] as const;

export type DeviceClass = (typeof DEVICE_CLASSES)[number];

/** What a device filter offers: the three real classes plus the null bucket. */
export const DEVICE_FILTER_KEYS = [...DEVICE_CLASSES, "unknown"] as const;

export function deviceLabel(value: string | null): string {
  switch (value) {
    case "desktop":
      return "Desktop";
    case "mobile":
      return "Mobile";
    case "tablet":
      return "Tablet";
    case "unknown":
    case null:
      return "Unknown";
    default:
      return value;
  }
}

/** The OS + browser under the class, as one line for the cell's sub-label.
 *  Empty when neither is recorded, so the caller renders nothing rather than a
 *  stray separator. */
export function deviceDetail(os: string | null, browser: string | null): string {
  return [browser, os]
    .filter((part): part is string => Boolean(part))
    .map(titleCase)
    .join(" on ");
}

function titleCase(value: string): string {
  switch (value) {
    case "ios":
      return "iOS";
    case "macos":
      return "macOS";
    case "chromeos":
      return "ChromeOS";
    default:
      return value.charAt(0).toUpperCase() + value.slice(1);
  }
}

/** The `?devices=` URL param: a comma list of device buckets.
 *
 *  Checked against the CLOSED vocabulary above, unlike the Explorer's stage ladder:
 *  the device classes are a set both sides own, so an unrecognized value cannot be
 *  a newer backend's rung — it can only be a typo or a stale link, and letting one
 *  through would narrow the cohort to nothing while the control showed no selection
 *  at all. Sorted + de-duplicated, so two spellings of one cohort are one URL, one
 *  query key, and one compute on the server. Absent when nothing survives, which is
 *  "every device". Shared by the Funnel and the Version Cohorts routes. */
export function parseDeviceParam(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const keys = value
    .split(",")
    .filter((key) => (DEVICE_FILTER_KEYS as readonly string[]).includes(key));
  const unique = [...new Set(keys)].sort();
  return unique.length > 0 ? unique.join(",") : undefined;
}

/** The options a table's device column offers in SERVER mode: the closed vocabulary
 *  plus the null bucket LAST, as the absence rather than a peer of the real classes.
 *  One constant, so the Searches list and the two worklists cannot list it three
 *  ways; the Explorer's own column filter derives the same set from the keys. */
export const DEVICE_FILTER_OPTIONS: readonly { value: string; label: string }[] =
  DEVICE_FILTER_KEYS.map((key) => ({ value: key, label: deviceLabel(key) }));
